import { asRecord } from './profileRuntime';

export type ProfilePurchasePorts = {
  apiUrl: string;
  request: (options: {
    url: string;
    method: 'DELETE' | 'POST';
    data?: Record<string, unknown>;
  }) => Promise<{ data?: unknown }>;
  setLoading: (loader: string, loading: boolean) => void;
  getUserById: (userId: string) => Promise<unknown>;
  setCurrentUser: (user: unknown) => void;
  emitUserChange: () => void;
};

function responseBody(value: unknown): Record<string, unknown> {
  const envelope = asRecord(value);
  if (!envelope) {
    return {};
  }
  const data = asRecord(envelope.data);
  if (!data) {
    return {};
  }
  return data;
}

function isEmpty(value: unknown): boolean {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0;
  }
  return false;
}

async function refreshMember(
  ports: ProfilePurchasePorts,
  userId: string,
  notify: boolean
): Promise<void> {
  ports.setLoading('getUser', true);
  try {
    const member = await ports.getUserById(userId);
    ports.setCurrentUser(isEmpty(member) ? null : member);
    if (notify) {
      ports.emitUserChange();
    }
  } catch {
    // The member refresh failure must not log the response: it can contain PII.
  } finally {
    ports.setLoading('getUser', false);
  }
}

/**
 * Profile-only cancel. Other screens keep `BookingService.cancelById`.
 * A successful cancel refreshes the member and emits userChange, matching that service.
 */
export async function cancelProfileBooking(
  ports: ProfilePurchasePorts,
  userId: string,
  bookingId: string
): Promise<Record<string, unknown>> {
  ports.setLoading('cancelBooking', true);
  try {
    const response = await ports.request({
      url: `${ports.apiUrl}bookings/${bookingId}`,
      method: 'DELETE',
    });
    ports.setLoading('cancelBooking', false);
    const body = responseBody(response);
    if (body.success === true) {
      await refreshMember(ports, userId, true);
    }
    return body;
  } catch (error) {
    return responseBody(error);
  } finally {
    ports.setLoading('cancelBooking', false);
  }
}

/**
 * Profile-only PAYG purchase. `buyMembershipHost.buyPlan` requires a plan code and
 * payment method, so it does not cover this call. Other screens keep `MembershipService.buyPlan`.
 */
export async function buyProfilePaygPlan(
  ports: ProfilePurchasePorts,
  userId: string
): Promise<Record<string, unknown>> {
  ports.setLoading('buyMembership', true);
  try {
    const response = await ports.request({
      url: `${ports.apiUrl}memberships/payg/purchase`,
      method: 'POST',
      data: {},
    });
    ports.setLoading('buyMembership', false);
    const body = responseBody(response);
    if (body.success === true) {
      await refreshMember(ports, userId, false);
    }
    return body;
  } catch (error) {
    return responseBody(error);
  } finally {
    ports.setLoading('buyMembership', false);
  }
}
